import type { DealerActivityType } from '../../models/entities/dealer-api.model';
import type {
  EventOutcomeGroup,
  EventOutcomeItem,
  EventOutcomePriority,
  EventOutcomesResponse,
} from '../../models/entities/event-outcomes.model';

const PRIORITY_IDS: readonly EventOutcomePriority[] = ['p1', 'p2', 'p3', 'p4', 'p5'];

const labelCollator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });

function compareOutcomeItems(a: EventOutcomeItem, b: EventOutcomeItem): number {
  if (!a.label !== !b.label) {
    return a.label ? -1 : 1;
  }

  return labelCollator.compare(a.label, b.label) || Number(a.id) - Number(b.id);
}

function toOutcomePriority(priorityId: number): EventOutcomePriority | null {
  if (!Number.isInteger(priorityId) || priorityId < 1 || priorityId > 5) {
    return null;
  }
  return PRIORITY_IDS[priorityId - 1] ?? null;
}

function mapDealerActivityTypesToOutcomeGroups(
  items: DealerActivityType[] | null,
  allowedPriorities?: ReadonlySet<EventOutcomePriority>,
): EventOutcomesResponse {
  if (!items?.length) {
    return { groups: [] };
  }

  const groupsByPriority = new Map<EventOutcomePriority, EventOutcomeItem[]>();

  for (const type of items) {
    const priority = toOutcomePriority(Number(type.priorityId));
    if (!priority || (allowedPriorities && !allowedPriorities.has(priority))) {
      continue;
    }

    const item: EventOutcomeItem = {
      id: String(type.id),
      label: type.name?.trim() ?? '',
      priority,
    };

    const existing = groupsByPriority.get(priority);
    if (existing) {
      existing.push(item);
    } else {
      groupsByPriority.set(priority, [item]);
    }
  }

  const groups: EventOutcomeGroup[] = [...groupsByPriority.entries()]
    .sort(([a], [b]) => PRIORITY_IDS.indexOf(a) - PRIORITY_IDS.indexOf(b))
    .map(([priority, groupItems]) => ({
      priority,
      items: [...groupItems].sort(compareOutcomeItems),
    }));

  return { groups };
}

/** Maps Dealer activity types into Raise-to-Resolve popover groups. */
export function mapDealerActivityTypesToRaiseOutcomes(
  items: DealerActivityType[] | null,
): EventOutcomesResponse {
  return mapDealerActivityTypesToOutcomeGroups(items);
}

/** Maps Dealer activity types into Close Event popover groups (P4 + P5 only). */
export function mapDealerActivityTypesToCloseOutcomes(
  items: DealerActivityType[] | null,
): EventOutcomesResponse {
  return mapDealerActivityTypesToOutcomeGroups(items, new Set(['p4', 'p5']));
}
